import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {environment} from '@environment';
import {Birthdate} from '@portal/models/birthdate';
import {LocalStorageType} from '@portal/models/local-storage-type';
import {ForgotPasswordRequest} from '@portal/models/request/forgot-password-request';
import {UserInfo} from '@portal/models/user-info';
import {BehaviorSubject, Observable, of, throwError, timer} from 'rxjs';
import {catchError, distinctUntilChanged, map, shareReplay, switchMap, take, tap, timeout} from 'rxjs/operators';
import {parseResponse} from '../utils/parse-response.operator';
import {LocalStorageService} from './providers/local-storage.service';

@Injectable()
export class PortalAuthService {
  private userInfoSubject$: BehaviorSubject<UserInfo | null | undefined> = new BehaviorSubject<UserInfo | null | undefined>(undefined);

  readonly userInfo$ = this.userInfoSubject$.asObservable();

  public lastForgotPasswordRequest: ForgotPasswordRequest;

  private lastRequest = new Date();

  public timeoutMinutes$: Observable<number>;

  private userInfoField$?: Observable<UserInfo | null>;

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
  ) {
    this.timeoutMinutes$ = timer(0, 1000).pipe(
      map(() => Math.max(0, environment.sessionTimeoutMinutes - Math.round((Date.now() - this.lastRequest.getTime()) / 1000 / 60))),
      distinctUntilChanged(),
    );
  }

  public login(email: string, password: string, body: any = {}): Observable<UserInfo> {
    const encodedCredentials = btoa(unescape(encodeURIComponent(`${email}:${password}`)));

    const headers = new HttpHeaders({Authorization: 'Basic '.concat(encodedCredentials)});
    const options = {
      headers,
    };

    return this.http.post<UserInfo>(environment.endpoints.login, body, options).pipe(
      parseResponse(UserInfo),
      tap(userInfo => this.updateUserInfo(userInfo)),
      map(userInfo => userInfo),
    );
  }

  public getUserInfo(): Observable<UserInfo | null> {
    if (!this.userInfoField$) {
      this.userInfoField$ = this.http.get<UserInfo>(environment.endpoints.userInfo).pipe(
        timeout(3000),
        catchError(err => {
          this.userInfoField$ = undefined;
          if (err?.status === 401) {
            return of(null);
          }
          if (err?.name === 'TimeoutError') {
            return throwError(() => new Error('server_timeout'));
          }
          return throwError(() => new Error(err?.status));
        }),
        parseResponse(UserInfo.nullable()),
        tap((userInfo: UserInfo) => {
          this.setLocalStorageUserInfo(userInfo);
          this.userInfoSubject$.next(userInfo);
        }),
        shareReplay(1),
      );
    }
    return this.userInfoField$;
  }

  public refreshUserInfo(): void {
    this.userInfoSubject$.next(null);
  }

  private updateUserInfo(userInfo: UserInfo): void {
    this.userInfoSubject$.next(userInfo);
    this.setLocalStorageUserInfo(userInfo);
  }

  private setLocalStorageUserInfo(userInfo: UserInfo): void {
    this.localStorageService.setItem('userInfo', JSON.stringify(userInfo), LocalStorageType.MANDATORY);
  }

  public logout(): void {
    this.userInfoSubject$.next(null);
    window.location.href = environment.endpoints.logout;
  }

  public requestPasswordReset(email: string, birthDate: Birthdate | undefined, postalCode: string): Observable<void> {
    const request: ForgotPasswordRequest = {email, birthDate, postalCode};
    this.lastForgotPasswordRequest = request;
    return this.http.post<void>(environment.endpoints.requestPasswordReset, request);
  }

  public resetPassword(token: string, newPassword: string, confirmNewPassword: string): Observable<void> {
    return this.http.post<void>(environment.endpoints.resetPassword, {token, newPassword, confirmNewPassword});
  }

  public isAuthenticated(): Observable<boolean> {
    return this.userInfo$.pipe(
      take(1),
      switchMap(userInfo => {
        if (userInfo === undefined) {
          return this.getUserInfo();
        }
        return of(userInfo);
      }),
      map(userInfo => !!userInfo),
    );
  }

  public isAuthenticationChecked(): Observable<boolean> {
    return this.userInfoSubject$.pipe(map(userInfo => userInfo !== undefined));
  }

  public isAuthenticatedValue(): boolean {
    return !!this.userInfoSubject$.getValue();
  }

  public updateLastRequest() {
    this.lastRequest = new Date();
  }

  public validateResetPasswordToken(token: string): Observable<void> {
    return this.http.post<void>(environment.endpoints.validateResetPasswordToken, token);
  }

  public requestPasswordResetLink(): Observable<void> {
    if (!this.lastForgotPasswordRequest) {
      return of();
    }

    return this.http.post<void>(environment.endpoints.requestPasswordResetLink, this.lastForgotPasswordRequest);
  }

  public changePassword(oldPassword: string, newPassword: string, confirmNewPassword: string): Observable<void> {
    const request = {
      oldPassword: btoa(unescape(encodeURIComponent(oldPassword))),
      newPassword: btoa(unescape(encodeURIComponent(newPassword))),
      confirmNewPassword: btoa(unescape(encodeURIComponent(confirmNewPassword))),
    };

    return this.http.put<void>(environment.endpoints.changePassword, request);
  }

  public updateVersionTermsOfUse(): Observable<void> {
    return this.http.put<void>(environment.endpoints.updateVersionTermsOfUse, {});
  }

  public checkEmailChangeToken(verificationToken: string): Observable<void> {
    const url = environment.endpoints.checkEmailChangeToken;
    return this.http.post<void>(url, verificationToken);
  }
}
